import { DestroyRef, Injectable, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";
import { Store } from "@ngrx/store";
import { Observable, Subject, defer, filter, finalize, fromEvent, of, share, take, timeout } from "rxjs";

import { RootState } from "../../store";
import * as AppActions from "../../store/app/actions";
import { WebConfig } from "../api";
import { UtilsService } from "../utils/utils.service";

type HostMessage =
  | { type: "JERI_PREVIEW_CONFIG"; payload: Partial<WebConfig> }
  | { type: "JERI_NAVIGATE"; payload: string }
  | { type: "JERI_TOKEN"; payload: { token: string | null } };

type OutgoingMessage =
  | { type: "READY" }
  | { type: "REGISTER" }
  | { type: "JERI_TOKEN_REQUEST" };

const TOKEN_REQUEST_TIMEOUT_MS = 5000;

@Injectable({ providedIn: "root" })
export class HostBridgeService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly store = inject<Store<RootState>>(Store);
  private readonly utilsService = inject(UtilsService);

  private readonly hostToken$ = new Subject<string | null>();
  private pendingToken$: Observable<string | null> | null = null;

  public start(): void {
    if (!this.utilsService.isInIframe()) return;

    fromEvent<MessageEvent>(window, "message")
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((event: MessageEvent) => event.source === window.parent),
        filter((event: MessageEvent): event is MessageEvent & { data: HostMessage } =>
          event.data?.type === "JERI_PREVIEW_CONFIG" || event.data?.type === "JERI_NAVIGATE" || event.data?.type === "JERI_TOKEN",
        ),
      )
      .subscribe(event => this.handleMessage(event.data));

    this.send({ type: "READY" });
  }

  public send(message: OutgoingMessage): void {
    if (!this.utilsService.isInIframe()) return;
    window.parent.postMessage(message, "*");
  }

  /** Asks the host for a fresh customer token. Emits null if not embedded, the host has no token, or it does not answer in time. */
  public requestToken(): Observable<string | null> {
    if (!this.utilsService.isInIframe()) return of(null);

    this.pendingToken$ ??= defer(() => {
      this.send({ type: "JERI_TOKEN_REQUEST" });
      return this.hostToken$;
    }).pipe(
      take(1),
      timeout({ first: TOKEN_REQUEST_TIMEOUT_MS, with: () => of(null) }),
      finalize(() => (this.pendingToken$ = null)),
      share(),
    );

    return this.pendingToken$;
  }

  private handleMessage(message: HostMessage): void {
    switch (message.type) {
      case "JERI_PREVIEW_CONFIG":
        if (!message.payload) return;
        this.store.dispatch(AppActions.previewWebConfig({ webConfig: message.payload }));
        return;
      case "JERI_NAVIGATE":
        if (typeof message.payload !== "string" || !message.payload) return;
        this.router.navigateByUrl(message.payload);
        return;
      case "JERI_TOKEN": {
        const token = message.payload?.token;
        if (token !== null && (typeof token !== "string" || !token)) return;
        this.hostToken$.next(token);
        return;
      }
    }
  }
}
